export type PortfolioSortMode =
  | 'recentlyAdded'
  | 'rating'
  | 'alpha'
  | 'abvAsc'
  | 'abvDesc';

export const PORTFOLIO_SORT_OPTIONS: Array<{
  value: PortfolioSortMode;
  label: string;
}> = [
  { value: 'recentlyAdded', label: 'Recently Added' },
  { value: 'rating', label: 'Rating' },
  { value: 'alpha', label: 'Alpha' },
  { value: 'abvAsc', label: 'ABV Low→High' },
  { value: 'abvDesc', label: 'ABV High→Low' },
];

export const DEFAULT_PORTFOLIO_SORT: PortfolioSortMode = 'recentlyAdded';

export interface PortfolioBeer {
  id?: string;
  name?: string;
  abv?: number;
  createdAt?: unknown;
  ratings?: { overall?: number };
}

function toMillis(value: unknown): number | null {
  if (value == null) {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (value instanceof Date) {
    return value.getTime();
  }
  if (
    typeof value === 'object' &&
    value !== null &&
    'toDate' in value &&
    typeof (value as { toDate: () => Date }).toDate === 'function'
  ) {
    const parsed = (value as { toDate: () => Date }).toDate();
    if (parsed instanceof Date && !Number.isNaN(parsed.getTime())) {
      return parsed.getTime();
    }
  }
  if (
    typeof value === 'object' &&
    value !== null &&
    'seconds' in value &&
    typeof (value as { seconds: number }).seconds === 'number'
  ) {
    return (value as { seconds: number }).seconds * 1000;
  }
  return null;
}

function toOverallRating(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }
  return value;
}

function toAbv(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }
  return value;
}

function compareNames(left: PortfolioBeer, right: PortfolioBeer): number {
  const nameLeft = (left.name || '').toLowerCase();
  const nameRight = (right.name || '').toLowerCase();
  return nameLeft.localeCompare(nameRight, undefined, { sensitivity: 'base' });
}

/**
 * Returns a new sorted array of beers for the given sort mode.
 */
export function sortPortfolioBeers(
  beers: PortfolioBeer[],
  sortMode: PortfolioSortMode,
): PortfolioBeer[] {
  if (!Array.isArray(beers) || beers.length === 0) {
    return [];
  }

  const sorted = [...beers];

  switch (sortMode) {
    case 'recentlyAdded':
      sorted.sort((beerA, beerB) => {
        const millisA = toMillis(beerA.createdAt);
        const millisB = toMillis(beerB.createdAt);
        if (millisA === null && millisB === null) {
          return compareNames(beerA, beerB);
        }
        if (millisA === null) {
          return 1;
        }
        if (millisB === null) {
          return -1;
        }
        if (millisB !== millisA) {
          return millisB - millisA;
        }
        return compareNames(beerA, beerB);
      });
      break;
    case 'rating':
      sorted.sort((beerA, beerB) => {
        const ratingA = toOverallRating(beerA.ratings?.overall);
        const ratingB = toOverallRating(beerB.ratings?.overall);
        if (ratingA === null && ratingB === null) {
          return compareNames(beerA, beerB);
        }
        if (ratingA === null) {
          return 1;
        }
        if (ratingB === null) {
          return -1;
        }
        if (ratingB !== ratingA) {
          return ratingB - ratingA;
        }
        return compareNames(beerA, beerB);
      });
      break;
    case 'alpha':
      sorted.sort(compareNames);
      break;
    case 'abvAsc':
      sorted.sort((beerA, beerB) => {
        const abvA = toAbv(beerA.abv);
        const abvB = toAbv(beerB.abv);
        if (abvA === null && abvB === null) {
          return compareNames(beerA, beerB);
        }
        if (abvA === null) {
          return 1;
        }
        if (abvB === null) {
          return -1;
        }
        if (abvA !== abvB) {
          return abvA - abvB;
        }
        return compareNames(beerA, beerB);
      });
      break;
    case 'abvDesc':
      sorted.sort((beerA, beerB) => {
        const abvA = toAbv(beerA.abv);
        const abvB = toAbv(beerB.abv);
        if (abvA === null && abvB === null) {
          return compareNames(beerA, beerB);
        }
        if (abvA === null) {
          return 1;
        }
        if (abvB === null) {
          return -1;
        }
        if (abvB !== abvA) {
          return abvB - abvA;
        }
        return compareNames(beerA, beerB);
      });
      break;
    default:
      sorted.sort(compareNames);
  }

  return sorted;
}
